import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { MaybeApiError } from '@resistapp/client/components/states/error';
import {
  useDeleteAdminShipmentDocument,
  useDownloadShipmentDocument,
  usePatchAdminShipment,
  useSendAdminShipmentDocumentEmail,
  useUploadAdminShipmentDocument,
} from '@resistapp/client/hooks/api';
import { useToastWithoutDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import { copyTextToClipboard } from '@resistapp/client/utils/clipboard';
import type {
  AdminShipmentResponse,
  ShipmentDocumentEmailRequest,
  ShipmentDocumentResponse,
} from '@resistapp/common/api-types';
import {
  buildShipmentDocumentEmailDraft,
  SHIPMENT_DOCUMENT_MAX_BYTES,
  SHIPMENT_DOCUMENT_TITLE_PRESETS,
} from '@resistapp/common/shipment-documents';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

type ShipmentDocumentCustomerModalProps = {
  shipment: AdminShipmentResponse | null;
  isOpen: boolean;
  onClose: () => void;
};

export function ShipmentDocumentCustomerModal({ shipment, isOpen, onClose }: ShipmentDocumentCustomerModalProps) {
  const downloadDocument = useDownloadShipmentDocument();
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<number | null>(null);
  const showToast = useToastWithoutDebounce();

  const handleDownload = (document: ShipmentDocumentResponse) => {
    if (!shipment) {
      return;
    }
    void (async () => {
      setDownloadingDocumentId(document.id);
      try {
        const blob = await downloadDocument.mutateAsync({
          shipmentId: shipment.id,
          documentId: document.id,
        });
        downloadBlob(blob, document.filename);
      } catch (error) {
        showToast({
          title: 'Download failed',
          description: error instanceof Error ? error.message : 'Please try again later.',
          status: 'error',
          duration: 7000,
        });
      } finally {
        setDownloadingDocumentId(null);
      }
    })();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{shipment ? `Shipment #${shipment.id} document centre` : 'Shipment document centre'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {shipment ? (
            <VStack align="stretch" spacing={5}>
              <Box>
                <Text fontWeight={600}>Pickup</Text>
                <Text>
                  {shipment.pickupDate}, {shipment.pickupWindowStart}–{shipment.pickupWindowEnd}
                </Text>
              </Box>

              {shipment.confirmationUrl ? (
                <Alert status="info" alignItems="center">
                  <AlertIcon />
                  <Flex align="center" justify="space-between" gap={3} width="100%" wrap="wrap">
                    <Box>
                      <Text fontWeight={600}>Confirm your pickup</Text>
                      <Text fontSize="sm">Open the transport provider’s confirmation page.</Text>
                    </Box>
                    <Button
                      as="a"
                      colorScheme="blue"
                      href={shipment.confirmationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Confirm pickup
                    </Button>
                  </Flex>
                </Alert>
              ) : (
                <Alert status="warning">
                  <AlertIcon />
                  The pickup confirmation link is being prepared.
                </Alert>
              )}

              <Box>
                <Text fontSize="lg" fontWeight={600} marginBottom={2}>
                  PDFs for this shipment
                </Text>
                {shipment.documents.length === 0 ? (
                  <Text color="gray.600">Your shipping documents are being prepared.</Text>
                ) : (
                  <VStack align="stretch" spacing={2}>
                    {shipment.documents.map(document => (
                      <DocumentRow
                        key={document.id}
                        document={document}
                        downloadLabel={document.title}
                        isDownloading={downloadingDocumentId === document.id}
                        onDownload={() => {
                          handleDownload(document);
                        }}
                      />
                    ))}
                  </VStack>
                )}
              </Box>
            </VStack>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function ShipmentDocumentAdminCentre({ shipment }: { shipment: AdminShipmentResponse }) {
  const queryClient = useQueryClient();
  const showToast = useToastWithoutDebounce();
  const patchShipment = usePatchAdminShipment();
  const uploadDocument = useUploadAdminShipmentDocument();
  const deleteDocument = useDeleteAdminShipmentDocument();
  const downloadDocument = useDownloadShipmentDocument();
  const sendEmail = useSendAdminShipmentDocumentEmail();

  const [confirmationUrl, setConfirmationUrl] = useState(shipment.confirmationUrl ?? '');
  const [titlePreset, setTitlePreset] = useState<(typeof SHIPMENT_DOCUMENT_TITLE_PRESETS)[number]>(
    SHIPMENT_DOCUMENT_TITLE_PRESETS[0],
  );
  const [customTitle, setCustomTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<number | null>(null);
  const documentCentreUrl = `${window.location.origin}/admin/sample-shipping?shipment=${shipment.id}`;
  const suggestedDraft = useMemo(
    () => buildShipmentDocumentEmailDraft({ ...shipment, documentCentreUrl }),
    [documentCentreUrl, shipment],
  );
  const [emailSubject, setEmailSubject] = useState(suggestedDraft.subject);
  const [emailBody, setEmailBody] = useState(suggestedDraft.body);
  const [emailDraftEdited, setEmailDraftEdited] = useState(false);
  const [emailPreview, setEmailPreview] = useState<ShipmentDocumentEmailRequest | null>(null);

  useEffect(() => {
    setConfirmationUrl(shipment.confirmationUrl ?? '');
  }, [shipment.confirmationUrl]);

  useEffect(() => {
    if (!emailDraftEdited) {
      setEmailSubject(suggestedDraft.subject);
      setEmailBody(suggestedDraft.body);
    }
  }, [emailDraftEdited, suggestedDraft]);

  const invalidateShipmentQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [`/admin/shipment/${shipment.id}`] }),
      queryClient.invalidateQueries({
        predicate: query =>
          typeof query.queryKey[0] === 'string' &&
          (query.queryKey[0].startsWith('/admin/shipments') || query.queryKey[0].startsWith('/shipments')),
      }),
      queryClient.invalidateQueries({
        predicate: query =>
          typeof query.queryKey[0] === 'string' &&
          query.queryKey[0].startsWith(`/admin/organisations/${shipment.organisation.id}/shipments`),
      }),
    ]);
  };

  const handleSaveConfirmationUrl = () => {
    void (async () => {
      try {
        await patchShipment.mutateAsync({
          shipmentId: shipment.id,
          body: { confirmationUrl: confirmationUrl.trim() || null },
        });
        await invalidateShipmentQueries();
        showToast({
          title: 'Confirmation link saved',
          status: 'success',
          duration: 4000,
        });
      } catch (error) {
        showToast({
          title: 'Failed to save confirmation link',
          description: error instanceof Error ? error.message : 'Please try again later.',
          status: 'error',
          duration: 7000,
        });
      }
    })();
  };

  const handleUpload = () => {
    const title = titlePreset === 'Other' ? customTitle.trim() : titlePreset;
    if (!file || !title) {
      showToast({
        title: 'Choose a PDF and title',
        status: 'warning',
        duration: 5000,
      });
      return;
    }

    void (async () => {
      try {
        const body = new FormData();
        body.append('title', title);
        body.append('file', file);
        await uploadDocument.mutateAsync({ shipmentId: shipment.id, body });
        await invalidateShipmentQueries();
        setFile(null);
        setCustomTitle('');
        setFileInputKey(value => value + 1);
        showToast({
          title: 'PDF uploaded',
          description: `${title} is now stored with shipment #${shipment.id}.`,
          status: 'success',
          duration: 5000,
        });
      } catch (error) {
        showToast({
          title: 'PDF upload failed',
          description: error instanceof Error ? error.message : 'Please try again later.',
          status: 'error',
          duration: 7000,
        });
      }
    })();
  };

  const handleDelete = (document: ShipmentDocumentResponse) => {
    if (!window.confirm(`Remove “${document.title}” from shipment #${shipment.id}?`)) {
      return;
    }
    void (async () => {
      try {
        await deleteDocument.mutateAsync({ shipmentId: shipment.id, documentId: document.id });
        await invalidateShipmentQueries();
        showToast({ title: 'Document removed', status: 'success', duration: 4000 });
      } catch (error) {
        showToast({
          title: 'Failed to remove document',
          description: error instanceof Error ? error.message : 'Please try again later.',
          status: 'error',
          duration: 7000,
        });
      }
    })();
  };

  const handleDownload = (document: ShipmentDocumentResponse) => {
    void (async () => {
      setDownloadingDocumentId(document.id);
      try {
        const blob = await downloadDocument.mutateAsync({
          shipmentId: shipment.id,
          documentId: document.id,
        });
        downloadBlob(blob, document.filename);
      } catch (error) {
        showToast({
          title: 'Download failed',
          description: error instanceof Error ? error.message : 'Please try again later.',
          status: 'error',
          duration: 7000,
        });
      } finally {
        setDownloadingDocumentId(null);
      }
    })();
  };

  const handleCopyEmail = () => {
    void (async () => {
      try {
        await copyTextToClipboard(`To: ${shipment.contactEmail}\nSubject: ${emailSubject}\n\n${emailBody}`);
        showToast({ title: 'Email text copied', status: 'success', duration: 4000 });
      } catch (error) {
        showToast({
          title: 'Failed to copy email text',
          description: error instanceof Error ? error.message : 'Please try again later.',
          status: 'error',
          duration: 7000,
        });
      }
    })();
  };

  const emailHasRequiredLinks =
    Boolean(shipment.confirmationUrl) &&
    emailBody.includes(shipment.confirmationUrl ?? '') &&
    emailBody.includes(documentCentreUrl);
  const canSendEmail =
    shipment.documentCentreVisible &&
    shipment.documents.length > 0 &&
    emailSubject.trim().length > 0 &&
    emailBody.trim().length > 0 &&
    emailHasRequiredLinks;

  const handleSendEmail = () => {
    if (!canSendEmail) {
      return;
    }
    setEmailPreview({
      recipientEmail: shipment.contactEmail,
      subject: emailSubject.trim(),
      body: emailBody.trim(),
    });
  };

  const handleConfirmSendEmail = () => {
    if (!emailPreview || sendEmail.isPending) {
      return;
    }
    void (async () => {
      try {
        await sendEmail.mutateAsync({
          shipmentId: shipment.id,
          body: emailPreview,
        });
        setEmailPreview(null);
        showToast({
          title: 'Shipping email submitted',
          description: `Submitted for delivery to ${emailPreview.recipientEmail}.`,
          status: 'success',
          duration: 6000,
        });
      } catch (error) {
        showToast({
          title: 'Could not confirm email submission',
          description: error instanceof Error ? error.message : 'Check delivery status before trying again.',
          status: 'error',
          duration: 7000,
        });
      }
    })();
  };

  return (
    <Box bg="white" borderRadius={6} boxShadow="sm" padding={5}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Text fontSize="xl" fontWeight={600}>
            Shipment document centre
          </Text>
          <Text color="gray.600" fontSize="sm">
            Store customer-facing shipping PDFs and prepare the pickup confirmation message.
          </Text>
        </Box>

        {shipment.documentCentreVisible ? (
          <Alert status="success">
            <AlertIcon />
            Customers with access to this workbook can see the confirmation button and uploaded PDFs.
          </Alert>
        ) : shipment.labWorkbookId ? (
          <Alert status="info">
            <AlertIcon />
            Hidden from customers while the shipment is {shipment.status}. It becomes visible at Scheduled, Processed,
            Picked up, and Received.
          </Alert>
        ) : (
          <Alert status="warning">
            <AlertIcon />
            This shipment has no lab workbook, so its document centre cannot be shared with a customer.
          </Alert>
        )}

        <FormControl>
          <FormLabel>Third-party pickup confirmation link</FormLabel>
          <Flex gap={2} direction={{ base: 'column', md: 'row' }}>
            <Input
              type="url"
              placeholder="https://transport-provider.example/confirm/..."
              value={confirmationUrl}
              onChange={event => {
                setConfirmationUrl(event.target.value);
              }}
            />
            <Button
              colorScheme="blue"
              variant="outline"
              isLoading={patchShipment.isPending}
              onClick={handleSaveConfirmationUrl}
            >
              Save link
            </Button>
          </Flex>
          <FormHelperText>Customers see this as a prominent “Confirm pickup” button.</FormHelperText>
          {shipment.confirmationUrl ? (
            <Link href={shipment.confirmationUrl} isExternal color="blue.700" fontSize="sm">
              Open saved confirmation link
            </Link>
          ) : null}
        </FormControl>

        <Divider />

        <Box>
          <Text fontSize="lg" fontWeight={600} marginBottom={3}>
            Shipping PDFs
          </Text>
          <VStack align="stretch" spacing={3}>
            <Flex gap={3} direction={{ base: 'column', md: 'row' }} align={{ md: 'flex-end' }}>
              <FormControl maxWidth={{ md: '260px' }}>
                <FormLabel>Download button title</FormLabel>
                <Select
                  value={titlePreset}
                  onChange={event => {
                    setTitlePreset(event.target.value as (typeof SHIPMENT_DOCUMENT_TITLE_PRESETS)[number]);
                  }}
                >
                  {SHIPMENT_DOCUMENT_TITLE_PRESETS.map(title => (
                    <option key={title} value={title}>
                      {title}
                    </option>
                  ))}
                </Select>
              </FormControl>
              {titlePreset === 'Other' ? (
                <FormControl>
                  <FormLabel>Custom title</FormLabel>
                  <Input
                    maxLength={120}
                    value={customTitle}
                    onChange={event => {
                      setCustomTitle(event.target.value);
                    }}
                  />
                </FormControl>
              ) : null}
              <FormControl>
                <FormLabel>PDF file</FormLabel>
                <Input
                  key={fileInputKey}
                  type="file"
                  accept="application/pdf,.pdf"
                  paddingTop={1}
                  onChange={event => {
                    setFile(event.target.files?.[0] ?? null);
                  }}
                />
              </FormControl>
              <Button
                colorScheme="blue"
                flexShrink={0}
                isLoading={uploadDocument.isPending}
                isDisabled={!file || (titlePreset === 'Other' && customTitle.trim().length === 0)}
                onClick={handleUpload}
              >
                Upload PDF
              </Button>
            </Flex>
            <Text color="gray.600" fontSize="xs">
              PDF only, maximum {formatFileSize(SHIPMENT_DOCUMENT_MAX_BYTES)} per file.
            </Text>

            {shipment.documents.length === 0 ? (
              <Text color="gray.600">No PDFs uploaded yet.</Text>
            ) : (
              <VStack align="stretch" spacing={2}>
                {shipment.documents.map(document => (
                  <DocumentRow
                    key={document.id}
                    document={document}
                    downloadLabel={document.title}
                    isDownloading={downloadingDocumentId === document.id}
                    onDownload={() => {
                      handleDownload(document);
                    }}
                    onDelete={() => {
                      handleDelete(document);
                    }}
                    isDeleting={deleteDocument.isPending && deleteDocument.variables.documentId === document.id}
                  />
                ))}
              </VStack>
            )}
          </VStack>
        </Box>

        <Divider />

        <Box>
          <Text fontSize="lg" fontWeight={600}>
            Customer email
          </Text>
          <Text color="gray.600" fontSize="sm" marginBottom={3}>
            Edit the suggested message, then either copy it into the existing customer thread or send it directly from
            the Platform. Platform send creates a new email from tech@resistomap.com, with secure document links and no
            attachments.
          </Text>
          <VStack align="stretch" spacing={3}>
            <FormControl>
              <FormLabel>To</FormLabel>
              <Input value={shipment.contactEmail} isReadOnly />
            </FormControl>
            <FormControl>
              <FormLabel>Subject</FormLabel>
              <Input
                maxLength={300}
                value={emailSubject}
                onChange={event => {
                  setEmailDraftEdited(true);
                  setEmailSubject(event.target.value);
                }}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Message</FormLabel>
              <Textarea
                minHeight="300px"
                maxLength={20_000}
                value={emailBody}
                onChange={event => {
                  setEmailDraftEdited(true);
                  setEmailBody(event.target.value);
                }}
              />
              <FormHelperText>
                Platform send requires the saved confirmation link and the Platform document link to remain in the
                message.
              </FormHelperText>
            </FormControl>
            <HStack flexWrap="wrap">
              <Button
                variant="outline"
                onClick={() => {
                  setEmailSubject(suggestedDraft.subject);
                  setEmailBody(suggestedDraft.body);
                  setEmailDraftEdited(false);
                }}
              >
                Use suggested text
              </Button>
              <Button variant="outline" onClick={handleCopyEmail}>
                Copy email text
              </Button>
              <Button
                colorScheme="blue"
                isLoading={sendEmail.isPending}
                isDisabled={!canSendEmail}
                onClick={handleSendEmail}
              >
                Send from Platform
              </Button>
            </HStack>
            {!canSendEmail ? (
              <Text color="gray.600" fontSize="sm">
                To enable Platform send, mark the shipment Scheduled or later, save the confirmation link, upload at
                least one PDF, and keep both links in the message.
              </Text>
            ) : null}
          </VStack>
        </Box>

        <MaybeApiError
          error={
            patchShipment.error ??
            uploadDocument.error ??
            deleteDocument.error ??
            downloadDocument.error ??
            sendEmail.error
          }
        />
      </VStack>
      <Modal
        isOpen={emailPreview !== null}
        onClose={() => {
          if (!sendEmail.isPending) {
            setEmailPreview(null);
          }
        }}
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Review customer email</ModalHeader>
          <ModalCloseButton isDisabled={sendEmail.isPending} />
          <ModalBody>
            {emailPreview ? (
              <VStack align="stretch" spacing={3}>
                <Text fontSize="sm" color="gray.600">
                  New email from tech@resistomap.com, not a reply to an existing thread. No attachments; PDFs are
                  downloaded through the secure Platform link.
                </Text>
                <Text>
                  <strong>To:</strong> {emailPreview.recipientEmail}
                </Text>
                <Text>
                  <strong>Subject:</strong> {emailPreview.subject}
                </Text>
                <Divider />
                <Text whiteSpace="pre-wrap" overflowWrap="anywhere">
                  {emailPreview.body}
                </Text>
                <MaybeApiError error={sendEmail.error} />
              </VStack>
            ) : null}
          </ModalBody>
          <ModalFooter gap={3}>
            <Button
              isDisabled={sendEmail.isPending}
              onClick={() => {
                setEmailPreview(null);
              }}
            >
              Back to editing
            </Button>
            <Button colorScheme="blue" isLoading={sendEmail.isPending} onClick={handleConfirmSendEmail}>
              Confirm and send
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

function DocumentRow({
  document,
  downloadLabel,
  isDownloading,
  onDownload,
  onDelete,
  isDeleting,
}: {
  document: ShipmentDocumentResponse;
  downloadLabel: string;
  isDownloading: boolean;
  onDownload: () => void;
  onDelete?: () => void;
  isDeleting?: boolean;
}) {
  return (
    <Flex
      align={{ base: 'stretch', md: 'center' }}
      border="1px solid"
      borderColor="gray.200"
      borderRadius={6}
      direction={{ base: 'column', md: 'row' }}
      gap={3}
      justify="space-between"
      padding={3}
    >
      <Box minWidth={0}>
        <Text fontWeight={600}>{document.title}</Text>
        <Text color="gray.600" fontSize="xs" overflowWrap="anywhere">
          {document.filename} · {formatFileSize(document.sizeBytes)}
        </Text>
        <Text color="gray.600" fontSize="xs">
          Uploaded {new Date(document.createdAt).toLocaleDateString()}
        </Text>
      </Box>
      <HStack maxWidth="100%">
        <Button
          size="sm"
          colorScheme="blue"
          variant="outline"
          whiteSpace="normal"
          height="auto"
          minHeight={8}
          paddingY={2}
          isLoading={isDownloading}
          onClick={onDownload}
        >
          {downloadLabel}
        </Button>
        {onDelete ? (
          <Button size="sm" colorScheme="red" variant="ghost" isLoading={isDeleting} onClick={onDelete}>
            Remove
          </Button>
        ) : null}
      </HStack>
    </Flex>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(bytes % (1024 * 1024) === 0 ? 0 : 1)} MB`;
  }
  return `${Math.max(1, Math.ceil(bytes / 1024))} KB`;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}
